import type {
  CogInputSchema,
  Deployment,
  Features,
  Model,
  OpenapiSchema,
  PlaygroundPermissions,
  Version,
} from "../../types";

export function getSortedInputProperties(
  properties: OpenapiSchema["components"]["schemas"]["Input"]["properties"]
) {
  return Object.keys(properties).sort((a, b) => {
    return properties[a]["x-order"] - properties[b]["x-order"];
  });
}

export function isImageUrl(url: string) {
  return /\.(jpeg|jpg|gif|png|webp)$/.test(url);
}

export function isImageMimeType(mimeType: string) {
  return /^image\/(jpeg|jpg|gif|png|x-png|webp)$/.test(mimeType);
}

export function basename(path: string) {
  return path.split(/[\\/]/).pop();
}

// Copied from https://npm.im/is-number
export function isNumber(num: any) {
  if (typeof num === "number") {
    return num - num === 0;
  }
  if (typeof num === "string" && num.trim() !== "") {
    return Number.isFinite ? Number.isFinite(+num) : Number.isFinite(+num);
  }
  return false;
}

export function nullishInputPredicate([key, value]: [string, any]) {
  return value != null;
}

export function safetyCheckerPredicate([key, _]: [string, any]) {
  return key !== "disable_safety_checker";
}

function validSchemaInputPredicate(schema: CogInputSchema["properties"]) {
  return ([key, _]: [string, any]) => {
    return key in schema;
  };
}

function hideDefaultValuesPredicate(schema: CogInputSchema["properties"]) {
  return ([key, value]: [string, any]) => {
    const defaultValue = schema[key].default;
    if (typeof defaultValue === "undefined") {
      return true;
    }
    if (Array.isArray(defaultValue)) {
      return !value.every((v: any, i: number) => v === defaultValue[i]);
    }
    return value !== defaultValue;
  };
}

export function cleanInputForSubmission(
  input: Record<string, any>,
  schema: CogInputSchema["properties"],
  hideDefaultValues?: boolean
) {
  return Object.fromEntries(
    Object.entries(input)
      .filter(nullishInputPredicate)
      .filter(safetyCheckerPredicate) // Prevent users from disabling the safety checker
      .filter(validSchemaInputPredicate(schema))
      .filter((value) => {
        if (hideDefaultValues) {
          return hideDefaultValuesPredicate(schema)(value);
        }
        return true;
      })
  );
}

/**
 * A helper function to handle special cases,
 * like string[] input types.
 */
export function processInputForSubmission(
  input: Record<string, any>,
  schema: CogInputSchema["properties"]
) {
  const processed: Record<string, any> = {};

  for (const [key, value] of Object.entries(input)) {
    const inputSchema = schema[key];

    // We have a special component in APIPlayground for handling string[] inputs.
    // It uses react-hook-form and converts string[] to { value: string }[].
    // On the way out, we need to convert any values back to string[].
    const isArrayOfStringType =
      "type" in inputSchema &&
      inputSchema.type === "array" &&
      inputSchema.items.type === "string";

    if (isArrayOfStringType && Array.isArray(value)) {
      const filtered = value.filter((v) => v != null);
      const values = filtered.map((v) => {
        if (typeof v === "object" && "value" in v) {
          return v.value;
        }
        return v;
      });
      processed[key] = values;
    } else {
      processed[key] = value;
    }
  }
  return processed;
}

export type RenderMode = "default" | "before-after-slider" | "goo-shader";

export function getRenderMode({
  features,
}: {
  features: Features;
}): RenderMode {
  if (features.show_goo_shader_output) {
    return "goo-shader";
  }

  if (features.show_before_after_slider_output) {
    return "before-after-slider";
  }

  return "default";
}

export interface ElementVisibility {
  tweakButton?: boolean;
}

export function getElementVisibility({
  permissions,
  deployment,
}: {
  permissions: PlaygroundPermissions;
  deployment?: Deployment;
  version?: Version;
  model?: Model;
}): ElementVisibility {
  return {
    tweakButton: permissions.tweak && !deployment,
  };
}
